import { Drawer, notification } from "antd";
import { get } from "lodash";
import PropTypes from "prop-types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MdAttachFile, MdFileDownload } from "react-icons/md";
import Loading from "shared/components/Spin";
import { useLazyQuery, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import {
  EMAIL_LOG,
  EMAIL_LOG_ATTACHMENT_URL,
} from "utils/api/graphql/queries/email-log";
import { formatDate } from "utils/helpers/date";

const drawerWidth = 1024;

const toDisplayList = (value) =>
  (Array.isArray(value) ? value : value ? [value] : [])
    .filter(Boolean)
    .join(", ");

const formatPersonName = (person) =>
  `${get(person, "firstname") || ""} ${get(person, "lastname") || ""}`.trim();

const formatFileSize = (size) => {
  if (!size) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

/** Links must leave the sandboxed frame instead of navigating inside it. */
const buildEmailDocument = (html) => `<base target="_blank">${html}`;

const EmailLogDetailRow = ({ label, value }) =>
  value ? (
    <div className="email-log-drawer__row">
      <span className="email-log-drawer__label">{label}</span>
      <span className="email-log-drawer__value">{value}</span>
    </div>
  ) : null;

EmailLogDetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};

EmailLogDetailRow.defaultProps = {
  value: "",
};

const EmailLogDrawer = ({ open, emailLogId, onClose }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTACT);
  const [downloadingKey, setDownloadingKey] = useState(null);

  const { data, loading, error } = useQuery(EMAIL_LOG, {
    skip: !open || !emailLogId,
    fetchPolicy: "no-cache",
    variables: { where: { id: emailLogId } },
  });

  const [fetchAttachmentUrl] = useLazyQuery(EMAIL_LOG_ATTACHMENT_URL, {
    fetchPolicy: "network-only",
    onError: () => {},
  });

  const emailLog = get(data, "emailLog");
  const attachments = get(emailLog, "attachments") || [];

  const notifyAttachmentError = () =>
    notification.error({
      message: t("ACTIVITIES.EMAIL.DRAWER.ATTACHMENT_ERROR"),
      closeIcon: null,
    });

  const handleOpenAttachment = async (attachment) => {
    if (attachment.url) {
      window.open(attachment.url, "_blank", "noopener,noreferrer");
      return;
    }
    if (!attachment.key || downloadingKey) return;
    setDownloadingKey(attachment.key);
    try {
      const { data: urlData } = await fetchAttachmentUrl({
        variables: { where: { id: emailLogId }, key: attachment.key },
      });
      const signedUrl = get(urlData, "emailLogAttachmentUrl");
      if (signedUrl) {
        window.open(signedUrl, "_blank", "noopener,noreferrer");
      } else {
        notifyAttachmentError();
      }
    } catch {
      notifyAttachmentError();
    } finally {
      setDownloadingKey(null);
    }
  };

  const renderBody = () => {
    if (loading) return <Loading />;
    if (error || !emailLog) {
      return (
        <div className="email-log-drawer__empty">
          {t("ACTIVITIES.EMAIL.DRAWER.LOAD_ERROR")}
        </div>
      );
    }
    const senderName = formatPersonName(emailLog.user);
    return (
      <div className="email-log-drawer__content d-flex flex--column gap-16">
        <div className="email-log-drawer__header d-flex flex--column gap-8">
          <h3 className="email-log-drawer__subject">
            {emailLog.subject || t("ACTIVITIES.EMAIL.DRAWER.NO_SUBJECT")}
          </h3>
          <EmailLogDetailRow
            label={t("ACTIVITIES.EMAIL.DRAWER.FROM")}
            value={emailLog.from}
          />
          <EmailLogDetailRow
            label={t("ACTIVITIES.EMAIL.DRAWER.TO")}
            value={toDisplayList(emailLog.to)}
          />
          <EmailLogDetailRow
            label={t("ACTIVITIES.EMAIL.DRAWER.CC")}
            value={toDisplayList(emailLog.cc)}
          />
          <EmailLogDetailRow
            label={t("ACTIVITIES.EMAIL.DRAWER.DATE")}
            value={emailLog.createdDate ? formatDate(emailLog.createdDate) : ""}
          />
          <EmailLogDetailRow
            label={t("ACTIVITIES.EMAIL.DRAWER.SENT_BY")}
            value={senderName}
          />
        </div>

        {attachments.length > 0 && (
          <div className="email-log-drawer__attachments d-flex flex--column gap-8">
            <span className="email-log-drawer__label">
              {t("ACTIVITIES.EMAIL.DRAWER.ATTACHMENTS", {
                count: attachments.length,
              })}
            </span>
            <div className="d-flex flex--wrap gap-8">
              {attachments.map((attachment, index) => {
                const isDownloadable = !!(attachment.key || attachment.url);
                return (
                  <button
                    key={attachment.key || attachment.url || index}
                    type="button"
                    className="email-log-drawer__attachment"
                    onClick={() => handleOpenAttachment(attachment)}
                    disabled={
                      !isDownloadable ||
                      (!!downloadingKey && downloadingKey === attachment.key)
                    }
                    title={
                      isDownloadable
                        ? t("ACTIVITIES.EMAIL.DRAWER.DOWNLOAD")
                        : t("ACTIVITIES.EMAIL.DRAWER.ATTACHMENT_UNAVAILABLE")
                    }
                  >
                    <MdAttachFile size={14} />
                    <span className="email-log-drawer__attachment-name">
                      {attachment.name}
                    </span>
                    {attachment.size ? (
                      <span className="email-log-drawer__attachment-size">
                        {formatFileSize(attachment.size)}
                      </span>
                    ) : null}
                    {isDownloadable && <MdFileDownload size={14} />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="email-log-drawer__body">
          {emailLog.html ? (
            <iframe
              className="email-log-drawer__frame"
              title={emailLog.subject || t("ACTIVITIES.EMAIL.DRAWER.TITLE")}
              sandbox="allow-popups allow-popups-to-escape-sandbox"
              srcDoc={buildEmailDocument(emailLog.html)}
            />
          ) : emailLog.text ? (
            <pre className="email-log-drawer__text">{emailLog.text}</pre>
          ) : (
            <div className="email-log-drawer__empty">
              {t("ACTIVITIES.EMAIL.DRAWER.NO_CONTENT")}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Drawer
      className="email-log-drawer"
      width={drawerWidth}
      title={t("ACTIVITIES.EMAIL.DRAWER.TITLE")}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      {open ? renderBody() : null}
    </Drawer>
  );
};

EmailLogDrawer.propTypes = {
  open: PropTypes.bool,
  emailLogId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

EmailLogDrawer.defaultProps = {
  open: false,
  emailLogId: null,
};

export default EmailLogDrawer;
